import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { useRealtimeConnection, CaptureEventData } from '@hooks/useRealtimeConnection';
import {
  ChatMessage,
  ConnectionStatus,
  ControlResult,
  ControlError,
  ControlQueueItem,
  RejectedItem,
  useRealtimeStore,
} from '@/stores/realtimeStore';
import { useUserStore } from '@/stores/userStore';

// ── 콜백 저장소 (모듈 스코프) ──
const chatCallbacks = new Map<number | null, (msg: ChatMessage) => void>();
let statusCallback: ((status: ConnectionStatus) => void) | null = null;
const controlResultCallbacks = new Map<number, (result: ControlResult) => void>();
const capturePendingCallbacks = new Set<(data: CaptureEventData) => void>();
const captureIdentifiedCallbacks = new Set<(data: CaptureEventData) => void>();
const captureExpiredCallbacks = new Set<(data: CaptureEventData) => void>();

// ── Context 타입 ──
interface RealtimeContextType {
  // 연결 상태 (채팅 useRoomConnection 호환)
  connectionStatus: ConnectionStatus;
  showConnectionError: boolean;
  reconnectAttempts: number;
  messages: ChatMessage[];
  messageContext: any;
  allMessageContexts: any[];
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  reconnect: () => void;
  registerMessageCallback: (roomId: number | null, cb: (msg: ChatMessage) => void) => void;
  unregisterMessageCallback: (roomId: number | null) => void;
  registerConnectionCallback: (cb: (status: ConnectionStatus) => void) => void;
  unregisterConnectionCallback: () => void;

  // 제어
  enqueueControl: (item: ControlQueueItem) => void;
  enqueueControlBatch: (items: ControlQueueItem[], rejected?: RejectedItem[]) => void;
  registerControlResultCallback: (commandId: number, cb: (result: ControlResult) => void) => void;
  unregisterControlResultCallback: (commandId: number) => void;

  // 캡처 이벤트
  registerCapturePendingCallback: (cb: (data: CaptureEventData) => void) => void;
  unregisterCapturePendingCallback: (cb: (data: CaptureEventData) => void) => void;
  registerCaptureIdentifiedCallback: (cb: (data: CaptureEventData) => void) => void;
  unregisterCaptureIdentifiedCallback: (cb: (data: CaptureEventData) => void) => void;
  registerCaptureExpiredCallback: (cb: (data: CaptureEventData) => void) => void;
  unregisterCaptureExpiredCallback: (cb: (data: CaptureEventData) => void) => void;
}

const RealtimeContext = createContext<RealtimeContextType | null>(null);

// ── Provider ──
export function RealtimeProvider({ children }: { children: ReactNode }) {
  // 채팅 메시지 핸들러
  const handleChatMessage = useCallback((msg: ChatMessage) => {
    chatCallbacks.forEach((cb, roomId) => {
      if (roomId === null || roomId === (msg as any).chatRoomId) {
        cb(msg);
      }
    });
  }, []);

  // 제어 결과 핸들러
  const handleControlResult = useCallback((result: ControlResult) => {
    const cb = controlResultCallbacks.get(result.commandId);
    if (cb) {
      cb(result);
      controlResultCallbacks.delete(result.commandId);
    }
  }, []);

  // 제어 에러 핸들러
  const handleControlError = useCallback((error: ControlError) => {
    if (error.commandId) {
      const cb = controlResultCallbacks.get(error.commandId);
      if (cb) {
        cb({
          commandId: error.commandId,
          nodeId: error.nodeId ?? 0,
          controlType: '',
          success: false,
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
        });
        controlResultCallbacks.delete(error.commandId);
      }
    }
  }, []);

  // 캡처 이벤트 핸들러
  const handleCapturePending = useCallback((data: CaptureEventData) => {
    capturePendingCallbacks.forEach(cb => cb(data));
  }, []);

  const handleCaptureIdentified = useCallback((data: CaptureEventData) => {
    captureIdentifiedCallbacks.forEach(cb => cb(data));
  }, []);

  const handleCaptureExpired = useCallback((data: CaptureEventData) => {
    captureExpiredCallbacks.forEach(cb => cb(data));
  }, []);

  // 연결 상태 핸들러
  const handleStatusChange = useCallback((status: string) => {
    statusCallback?.(status as ConnectionStatus);
  }, []);

  // 인증된 사용자만 SSE 연결
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);

  const connection = useRealtimeConnection({
    enabled: isAuthenticated,
    onChatMessage: handleChatMessage,
    onControlResult: handleControlResult,
    onControlError: handleControlError,
    onCapturePending: handleCapturePending,
    onCaptureIdentified: handleCaptureIdentified,
    onCaptureExpired: handleCaptureExpired,
    onStatusChange: handleStatusChange,
  });

  // ── 채팅 콜백 등록 (기존 API 유지) ──
  const registerMessageCallback = useCallback(
    (roomId: number | null, cb: (msg: ChatMessage) => void) => {
      chatCallbacks.set(roomId, cb);
    },
    [],
  );
  const unregisterMessageCallback = useCallback((roomId: number | null) => {
    chatCallbacks.delete(roomId);
  }, []);
  const registerConnectionCallback = useCallback(
    (cb: (status: ConnectionStatus) => void) => {
      statusCallback = cb;
    },
    [],
  );
  const unregisterConnectionCallback = useCallback(() => {
    statusCallback = null;
  }, []);

  // ── 제어 큐 관리 ──
  const enqueueControl = useCallback((item: ControlQueueItem) => {
    useRealtimeStore.getState().addControlCommand(item);
  }, []);

  const enqueueControlBatch = useCallback(
    (items: ControlQueueItem[], rejected?: RejectedItem[]) => {
      useRealtimeStore.getState().addControlCommands(items);
      if (rejected && rejected.length > 0) {
        useRealtimeStore.getState().addRejectedItems(rejected);
      }
    },
    [],
  );

  const registerControlResultCallback = useCallback(
    (commandId: number, cb: (result: ControlResult) => void) => {
      controlResultCallbacks.set(commandId, cb);
    },
    [],
  );

  const unregisterControlResultCallback = useCallback((commandId: number) => {
    controlResultCallbacks.delete(commandId);
  }, []);

  // ── 캡처 콜백 등록 ──
  const registerCapturePendingCallback = useCallback((cb: (data: CaptureEventData) => void) => {
    capturePendingCallbacks.add(cb);
  }, []);
  const unregisterCapturePendingCallback = useCallback((cb: (data: CaptureEventData) => void) => {
    capturePendingCallbacks.delete(cb);
  }, []);
  const registerCaptureIdentifiedCallback = useCallback((cb: (data: CaptureEventData) => void) => {
    captureIdentifiedCallbacks.add(cb);
  }, []);
  const unregisterCaptureIdentifiedCallback = useCallback((cb: (data: CaptureEventData) => void) => {
    captureIdentifiedCallbacks.delete(cb);
  }, []);
  const registerCaptureExpiredCallback = useCallback((cb: (data: CaptureEventData) => void) => {
    captureExpiredCallbacks.add(cb);
  }, []);
  const unregisterCaptureExpiredCallback = useCallback((cb: (data: CaptureEventData) => void) => {
    captureExpiredCallbacks.delete(cb);
  }, []);

  const value: RealtimeContextType = {
    connectionStatus: connection.connectionStatus,
    showConnectionError: connection.showConnectionError,
    reconnectAttempts: connection.reconnectAttempts,
    messages: connection.messages,
    messageContext: connection.messageContext,
    allMessageContexts: connection.allMessageContexts,
    isConnected: connection.isConnected,
    connect: connection.connect,
    disconnect: connection.disconnect,
    reconnect: connection.reconnect,
    registerMessageCallback,
    unregisterMessageCallback,
    registerConnectionCallback,
    unregisterConnectionCallback,
    enqueueControl,
    enqueueControlBatch,
    registerControlResultCallback,
    unregisterControlResultCallback,
    registerCapturePendingCallback,
    unregisterCapturePendingCallback,
    registerCaptureIdentifiedCallback,
    unregisterCaptureIdentifiedCallback,
    registerCaptureExpiredCallback,
    unregisterCaptureExpiredCallback,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

// ── 훅: Context 접근 ──
export function useRealtimeContext() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtimeContext must be used within RealtimeProvider');
  return ctx;
}

// ── 훅: 채팅 (기존 useRoomConnection 호환) ──
export interface UseRoomConnectionProps {
  roomId: number | null;
  onMessageReceived?: (msg: ChatMessage) => void;
  onConnectionStatusChange?: (status: ConnectionStatus) => void;
}

export function useRoomConnection({
  roomId,
  onMessageReceived,
  onConnectionStatusChange,
}: UseRoomConnectionProps) {
  const ctx = useRealtimeContext();

  useEffect(() => {
    if (onMessageReceived) {
      ctx.registerMessageCallback(roomId, onMessageReceived);
      return () => ctx.unregisterMessageCallback(roomId);
    }
  }, [ctx, roomId, onMessageReceived]);

  useEffect(() => {
    if (onConnectionStatusChange) {
      ctx.registerConnectionCallback(onConnectionStatusChange);
      return () => ctx.unregisterConnectionCallback();
    }
  }, [ctx, onConnectionStatusChange]);

  return {
    connectionStatus: ctx.connectionStatus,
    showConnectionError: ctx.showConnectionError,
    reconnectAttempts: ctx.reconnectAttempts,
    isConnected: ctx.isConnected,
    reconnect: ctx.reconnect,
    connect: ctx.connect,
    disconnect: ctx.disconnect,
  };
}

// ── 훅: 제어 큐 (액션만 — store 구독 없음) ──
export function useControlQueue() {
  const ctx = useRealtimeContext();

  return {
    enqueueControl: ctx.enqueueControl,
    enqueueControlBatch: ctx.enqueueControlBatch,
    registerControlResultCallback: ctx.registerControlResultCallback,
    unregisterControlResultCallback: ctx.unregisterControlResultCallback,
  };
}
